import { fromEntries } from "src/utils/typeWrappers";

import MealFeedbackHeroImage from "@components/intro/images/meal-feedback.webp";
import WelliInsightHeroImage from "@components/intro/images/welli-insight.webp";
import MethodSelectorHeroImage from "@components/intro/images/method-selector.webp";
import WelliInsightGlp1CompanionHeroImage from "@components/intro/images/welli-insight-glp1-companion.webp";
import FoodHeroImage from "@components/intro/images/food.webp";
import MoveWeightLossNoMedHeroImage from "@components/intro/images/move-weight-loss-no-med.webp";
import StressGlp1CompanionHeroImage from "@components/intro/images/stress-glp1-companion.webp";
import LibraryDefaultHeroImage from "@components/intro/images/library-default.webp";

export type InterstitialContext = (typeof CONTEXTS)[number];
export type InterstitialVersion = (typeof VERSIONS)[number];

const translationPrefix = "t:mealLoggingInterstitial";

/** Success Kit sectionId → interstitial context. The full UTM string emitted
 *  by the backend `SuccessKitUpsellAdEnricher` has the shape
 *  `success_kit_<sectionId>_<med|weight>`, where `<sectionId>` is one of
 *  the keys below. This map is the single source of truth for both:
 *    1. `ALLOWED_UTM_SOURCES` (the gate in `shouldShowMealLoggingInterstitial`)
 *    2. `getMealLoggingInterstitialContext()` (the routing in segments.ts)
 *  Adding a new SK section is a one-line change here — both layers stay in
 *  sync automatically. Keep aligned with the backend sectionId allowlist. */
export const SK_SECTION_TO_CONTEXT = {
  eat_healthier: "food",
  move_your_body: "move",
  manage_strong_emotions: "stress",
  create_inner_calm: "stress",
} as const;

/** Anchored regex matching the Success Kit UTM shape. Capture group 1 is
 *  the sectionId (looked up in `SK_SECTION_TO_CONTEXT`); capture group 2 is
 *  the variant (`med` or `weight`), currently used only for gating. */
export const SK_UTM_REGEX = /^success_kit_(.+)_(med|weight)$/;

const SK_UTM_SOURCES = Object.keys(SK_SECTION_TO_CONTEXT).flatMap(
  (sectionId) => [
    `success_kit_${sectionId}_med`,
    `success_kit_${sectionId}_weight`,
  ]
);

/** UTM sources that cause the user to get redirected to the meal logging interstitial page */
export const ALLOWED_UTM_SOURCES = [
  // Method selector UTMs
  "meal_logging_method_selector_history_med_upgrade",
  "meal_logging_method_selector_history_weight_upgrade",
  "meal_logging_method_selector_barcode_med_upgrade",
  "meal_logging_method_selector_barcode_weight_upgrade",
  "meal_logging_method_selector_search_med_upgrade",
  "meal_logging_method_selector_search_weight_upgrade",
  // Method selector fallback UTMs
  "meal_feedback_med_upgrade",
  "meal_feedback_weight_upgrade",
  // Meal feedback content UTMs
  "meal_feedback_content_med_upgrade",
  "meal_feedback_content_weight_upgrade",
  "meal_feedback_content_weight_protect_muscles_glp1_companion",
  // Welli meal insight UTMs
  "welli_meal_insight_med_upgrade",
  "welli_meal_insight_weight_upgrade",
  // Success Kit section-derived UTMs (OTC-2248, OTC-2313). Derived from
  // `SK_SECTION_TO_CONTEXT` so the gate and the routing in segments.ts
  // cannot drift apart when a new section is added.
  ...SK_UTM_SOURCES,
  // Library module upsell UTMs
  "module_library_med",
  "module_library_weight",
];

/*
 * BEFORE ADDING NEW CONTEXT OR VERSION:
 * 1. Add localization strings to src/locales/en-US/translations.json
 * 2. Add relevant UTM sources to ALLOWED_UTM_SOURCES
 * 3. Add detection logic to getMealLoggingInterstitialContext() and getMealLoggingInterstitialVersion()
 */
export const CONTEXTS = [
  "methodSelector" /** Users coming from meal logging method selector screens */,
  "mealFeedback" /** Users coming from meal feedback content */,
  "welliInsight" /** Users coming from Welli AI meal insight features */,
  "food" /** Users coming from success kit "eat healthier" food upsell */,
  "move" /** Users coming from success kit "move" upsell */,
  "stress" /** Users coming from success kit "stress tools" upsell */,
  "library" /** Users coming from library module upsell */,
] as const;

export const VERSIONS = [
  "noWeightLoss" /** Users without weight loss goal */,
  "weightLossNoMed" /** Users with weight loss goal, BMI below context threshold */,
  "weightLossMed" /** Users with weight loss goal, BMI at or above context threshold */,
  "glp1Companion" /** Users on Free GLP-1 Companion (medEntitlements.hasMedCompanion) */,
] as const;

/** BMI threshold at which a user with a weight loss goal is considered med-eligible
 *  for the `weightLossMed` variant. New GLP-1-aware contexts use 21; existing
 *  contexts keep the historical 25. */
export const MED_BMI_THRESHOLD: Record<InterstitialContext, number> = {
  methodSelector: 25,
  mealFeedback: 25,
  welliInsight: 25,
  food: 21,
  move: 21,
  stress: 21,
  library: 21,
};

/** Contexts whose Companion override is gated on
 *  `medEntitlements.loadStatus === "success"` (the OTC-2248 race-safe path):
 *  if entitlements haven't resolved yet, the segment skips the interstitial
 *  rather than risk showing the wrong variant. Existing contexts (e.g.
 *  `welliInsight`) still surface the `glp1Companion` variant via the eager
 *  `isMedicationCompanionUser()` check, so this set is *not* "every context
 *  that can return glp1Companion" — only those that opt into loadStatus
 *  gating. */
export const GLP1_COMPANION_CONTEXTS: ReadonlySet<InterstitialContext> =
  new Set(["food", "move", "stress", "library"]);

/** Contexts that should be tracked under the new `OnUpsellInterstitialPage`
 *  Mixpanel event (per OTC-2039) instead of the legacy
 *  `OnMealLoggingInterstitialPage`. The downstream Mixpanel transform maps
 *  pageId → event name (same convention as `welcomeToNoom` → `OnWelcomeToNoom`
 *  in src/utils/monitoring/braze.ts). */
export const UPSELL_INTERSTITIAL_CONTEXTS: ReadonlySet<InterstitialContext> =
  new Set(["food", "move", "stress", "library"]);

/** pageId fired in `BuyflowPageViewed` for contexts in
 *  `UPSELL_INTERSTITIAL_CONTEXTS`. Mixpanel derives `OnUpsellInterstitialPage`
 *  from this. */
export const UPSELL_INTERSTITIAL_PAGE_ID = "upsellInterstitial";

/** pageId fired in `BuyflowPageViewed` for the legacy contexts. Mixpanel
 *  derives `OnMealLoggingInterstitialPage` from this. */
export const MEAL_LOGGING_INTERSTITIAL_PAGE_ID = "mealLoggingInterstitial";

/** Resolves the analytics pageId for an interstitial context. */
export function getInterstitialPageId(context: InterstitialContext): string {
  return UPSELL_INTERSTITIAL_CONTEXTS.has(context)
    ? UPSELL_INTERSTITIAL_PAGE_ID
    : MEAL_LOGGING_INTERSTITIAL_PAGE_ID;
}

type ContextHeroImages = {
  default: any;
} & Partial<Record<InterstitialVersion, any>>;

export const HERO_IMAGES: Record<InterstitialContext, ContextHeroImages> = {
  methodSelector: { default: MethodSelectorHeroImage },
  mealFeedback: { default: MealFeedbackHeroImage },
  welliInsight: {
    default: WelliInsightHeroImage,
    glp1Companion: WelliInsightGlp1CompanionHeroImage,
  },
  food: { default: FoodHeroImage },
  move: {
    default: MoveWeightLossNoMedHeroImage,
  },
  stress: {
    default: StressGlp1CompanionHeroImage,
  },
  library: {
    default: LibraryDefaultHeroImage,
  },
};

function getHeroImage(
  context: InterstitialContext,
  version: InterstitialVersion
) {
  return HERO_IMAGES[context][version] ?? HERO_IMAGES[context].default;
}

export const CONTENT_CONFIG = fromEntries(
  CONTEXTS.map((context) => [
    context,
    fromEntries(
      VERSIONS.map((version) => [
        version,
        {
          title: `${translationPrefix}:${context}:${version}:title`,
          cta: `${translationPrefix}:${context}:${version}:cta`,
          heroImage: getHeroImage(context, version),
          bullets: [
            `${translationPrefix}:${context}:${version}:bullet1`,
            `${translationPrefix}:${context}:${version}:bullet2`,
            `${translationPrefix}:${context}:${version}:bullet3`,
          ],
        } as const,
      ])
    ),
  ])
);
